import { Info, SettingsOutlined } from "@mui/icons-material";
import {
  AppBar,
  Badge,
  Drawer,
  FormControl,
  Icon,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  Menu,
  MenuItem,
  Paper,
  styled,
  Typography,
} from "@mui/material";

const headerHeight = 65;
const drawerWidth = 250;

export const StyledAuthRoutes = {
  Content: styled("main")(({ theme, open }) => ({
    flex: 1,
    marginTop: `${headerHeight}px`,
    padding: 15,
    overflow: "auto",
    maxWidth: open
      ? `calc(100vw - ${drawerWidth}px)`
      : `calc(100vw - ${theme.spacing(8)})`,
  })),
  Root: styled("div")(({ theme }) => ({
    display: "flex",
    flex: 1,
    backgroundColor: "#f5f6fa",
  })),
};

export const StyledPublicRoutes = {
  Root: styled("div")(({ theme }) => ({
    display: "flex",
    flex: 1,
    backgroundColor: "#f5f6fa",
  })),
  Content: styled("main")(({ theme }) => ({
    flexGrow: 1,
    padding: theme.spacing(2),
    marginTop: `${headerHeight}px`,
    overflow: "auto",
    width: theme.spacing(1),
  })),
};

export const StyledHeader = {
  AppBar: styled(AppBar)(({ theme, open }) => ({
    boxShadow: "none",
    backgroundColor: "#ffffff",
    zIndex: 1202,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    height: headerHeight,
    marginLeft: open ? `${drawerWidth}px` : 0,
    width: open ? `calc(100% - ${drawerWidth}px)` : "100%",
  })),
  IconButton: styled(IconButton)(({ open }) => ({
    marginRight: "36px",
    color: "rgb(158, 158, 157)",
    display: open ? "none" : "inline-flex",
  })),
  FormControl: styled(FormControl)({
    width: 300,
  }),
  Option: styled(MenuItem)({
    fontSize: 16,
  }),
  HeaderIcon: styled(IconButton)(({ theme }) => ({
    marginLeft: 5,
    color: "#858a93",
  })),
  MenuItem: styled(ListItemIcon)(({ theme }) => ({
    marginRight: 1,
  })),
  OptionsIconContainer: styled("div")(({ theme }) => ({
    display: "flex",
  })),
  Menu: styled(Menu)(({ theme }) => ({
    marginTop: 40,
    marginLeft: 0,
  })),
  InfoMessageTitle: styled(Info)(({ theme }) => ({
    marginBottom: -7,
    marginRight: 10,
  })),
  MessageContainer: styled(Paper)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    margin: 20,
    marginLeft: 13,
    marginRight: 13,
    backgroundColor: theme.palette.primary.main,
    border: "1px solid #539aff",
    borderRadius: 5,
    padding: "15px 15px 5px 20px",
    color: "white",
    cursor: "pointer",
  })),
  BottomText: styled(Typography)(({ theme }) => ({
    alignSelf: "center",
    marginTop: 20,
  })),
  MiddleText: styled(Typography)(({ theme }) => ({
    alignSelf: "center",
  })),
};

export const StyledDrawer = {
  Drawer: styled(Drawer)(({ theme, open }) => ({
    width: open ? drawerWidth : theme.spacing(8),
    flexShrink: 0,
    whiteSpace: "nowrap",
    "& .MuiDrawer-paper": {
      width: open ? drawerWidth : theme.spacing(8),
      transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: open
          ? theme.transitions.duration.enteringScreen
          : theme.transitions.duration.leavingScreen,
      }),
      scrollbarWidth: "none",
      backgroundColor: "#ffffff",
      borderRight: 0,
      marginTop: open ? 0 : "65px", // Espacio para evitar superposición
    },
  })),
  Toolbar: styled("div")(({ open }) => ({
    padding: 15,
    backgroundColor: "#ffffff",
    display: open ? "flex" : "none",
    justifyContent: "space-evenly",
    alignContent: "center",
    overflow: "hidden",
  })),
  Logo: styled("img")({
    height: "fit-content",
    marginBottom: 5,
  }),
  Root: styled(List)(({ theme, open }) => ({
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    maxWidth: 360,
    backgroundColor: "#ffffff",
    fontSize: 13.5,
    marginTop: 0,
    overflowY: "auto !important",
    overflowX: "hidden !important",
    scrollbarWidth: "none",
    marginBottom: open ? 0 : 65,
  })),
  Nested: styled("div")(({ theme }) => ({
    color: theme.palette.primary.backgroundListItem,
  })),
  NodeParent: styled(ListItem)(({ theme }) => ({
    borderRadius: "7px",
    width: "100%",
    display: "flex",
    margin: "0px 0px 0px 5px !important",
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: "8px",
  })),
  ExpandArrow: styled(Icon)(({ theme }) => ({
    color: theme.palette.primary.backgroundListItem,
  })),
  NodeParentText: styled("p")(({ theme }) => ({
    color: theme.palette.primary.backgroundListItem,
    fontFamily: "Roboto, sans-serif",
    margin: "0 0 0 0",
    fontWeight: "bold",
    flex: 1,
  })),
  Icon: styled(Icon)(({ theme }) => ({
    [theme.breakpoints.up("sm")]: {
      minWidth: 20,
    },
  })),
  Badge: styled(Badge)(({ theme }) => ({
    marginRight: 10,
  })),
  ConfigurationIcon: styled(SettingsOutlined)({
    margin: "0 0 0 0",
  }),
  SubItemIcon: styled(ListItemIcon)(({ open }) => ({
    marginLeft: open ? "5px" : "0px",
  })),
  IndentedContainer: styled("div")(({ open }) => ({
    marginLeft: open ? "15px" : "0px",
  })),
};

export const StyledListItem = {
  Nested: styled(ListItem)(({ theme, itemSelected }) => ({
    margin: "0px 0px 0px 5px !important",
    backgroundColor: itemSelected
      ? theme.palette.primary.main
      : theme.palette.primary.contrastText,
    borderRadius: "7px",
    "&:hover": {
      backgroundColor: (props) =>
        props.selected ? theme.palette.primary.main : "rgba(0, 0, 0, 0.04)",
    },
  })),
  Text: styled(Badge)(({ theme, selected }) => ({
    color: selected
      ? theme.palette.primary.contrastText
      : theme.palette.primary.backgroundListItem,
    fontFamily: "Roboto, sans-serif",
    margin: 0,
  })),
  Icon: styled(ListItemIcon)(({ theme, selected }) => ({
    color: selected
      ? theme.palette.primary.contrastText
      : theme.palette.primary.backgroundListItem,
  })),
};

export const StyledListSubItem = {
  Nested: styled(ListItem)(({ theme }) => ({
    borderRadius: "7px !important",
    [theme.breakpoints.up("sm")]: {
      paddingLeft: theme.spacing(2),
    },
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.04)",
    },
  })),
  List: styled(List)(({ theme, itemSelected, open }) => ({
    backgroundColor: itemSelected
      ? theme.palette.primary.main
      : theme.palette.primary.contrastText,
    color: theme.palette.primary.contrastText,
    borderRadius: "7px !important",
    margin: "5px !important",
    marginLeft: open ? "25px !important" : "5px !important",
  })),
  ListIcon: styled(ListItemIcon)(({ theme, itemSelected }) => ({
    "& svg": {
      color: itemSelected
        ? theme.palette.primary.contrastText
        : theme.palette.primary.backgroundListItem,
      fontSize: "24px", // Tamaño del ícono principal
    },
  })),
  RightIcon: styled(ListItemIcon)(({ theme, itemSelected }) => ({
    "& svg": {
      color: itemSelected
        ? theme.palette.primary.contrastText
        : theme.palette.primary.main,
      fontSize: "20px", // Tamaño del ícono derecho
    },
    marginLeft: 30,
  })),
  Text: styled("p")(({ theme, itemSelected }) => ({
    color: itemSelected
      ? theme.palette.primary.contrastText
      : theme.palette.primary.backgroundListItem,
    fontFamily: "Roboto, sans-serif",
    margin: 0,
  })),
  TitleContainer: styled("div")(({ theme }) => ({
    lineHeight: 1.3,
  })),
};
